/**
 * Google Analytics 4 helper functions for DMARC Guardian
 *
 * GDPR COMPLIANCE: Google Consent Mode v2 is used.
 * The gtag.js script is loaded in index.html with all consent signals defaulting
 * to 'denied'. Consent is updated via gtag('consent', 'update', ...) when the
 * user makes a choice in the cookie banner (no page reload required).
 */

export const GA_TRACKING_ID = 'G-Q504W7B6RH'

/**
 * The org's current `subscription_type`, fed in by the caller from the same
 * source every billing surface reads (`BillingContext` → the
 * `/api/v1/billing/status` response) — never re-derived independently here.
 * `undefined` until the first billing-status fetch resolves.
 *
 * Why this exists (86cb3bbww, user-approved 2026-08-10): with zero real
 * customers today, the two `comp` (Founders) internal accounts ARE the
 * dataset. Activation/retention/conversion metrics computed largely from
 * the product owners using their own product are worse than having no
 * metrics — they look plausible while being wrong. `isCompOrg()` below is
 * the one choke point every funnel-tracking function checks, instead of
 * each call site re-deriving "is this a comp org" itself (same reasoning
 * as `resolvePlanPresentation`).
 */
let currentSubscriptionType: string | undefined

/**
 * Feed the org's subscription_type into the analytics module. Call this
 * whenever `useBilling().status?.subscription_type` changes (see
 * `main.tsx` `RootLayout`) — analytics must never read billing status
 * through a second, independent path.
 */
export const setAnalyticsSubscriptionType = (
  subscriptionType: string | undefined
): void => {
  currentSubscriptionType = subscriptionType
}

/**
 * True for orgs whose conversion/funnel events must be suppressed.
 *
 * Scope: only the revenue-funnel categories (`trackBillingEvent` —
 * event_category 'billing' — and `trackConversion` — event_category
 * 'conversion') are gated. Product-usage and diagnostic telemetry
 * (page views, clicks, domain add/delete, errors) are deliberately left
 * untouched: the two owners are currently the most active real users of
 * the product, and losing their error reports or usage signal would be a
 * real loss with no metrics-integrity benefit — only the events that feed
 * activation/retention/trial→paid conversion numbers are at risk of being
 * skewed by two internal accounts.
 */
function isCompOrg(): boolean {
  return currentSubscriptionType === 'comp'
}

// Salt for hashing user IDs (should be kept secret in production)
const USER_ID_SALT =
  import.meta.env.VITE_USER_ID_SALT || 'dmarc-guardian-salt-2025'

export interface EventParams {
  [key: string]: string | number | boolean | undefined
}

export interface UserProperties {
  user_id?: string
  user_role?: string
  org_id?: string
  org_name?: string
  subscription_type?: string
  plan_name?: string
  billing_cycle?: string
}

/**
 * Hash a user ID for privacy (GDPR/CCPA pseudonymization)
 */
async function hashUserId(userId: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(userId + USER_ID_SALT)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Check if analytics consent has been granted
 * @returns true if user has consented to analytics, false otherwise
 */
export const hasAnalyticsConsent = (): boolean => {
  try {
    const consent = localStorage.getItem('dmarc-guardian-cookie-consent')
    if (!consent) return false
    const parsed = JSON.parse(consent)
    return parsed.analytics === true
  } catch {
    return false
  }
}

/**
 * Update Google Consent Mode v2 signals.
 * Called when the user accepts or declines cookies.
 * Ad signals are always kept denied — this app does not use advertising.
 */
export const updateConsentMode = (analyticsGranted: boolean): void => {
  if (typeof window === 'undefined' || !window.gtag) return
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
}

/**
 * Check that GA is available and user has consented.
 * All tracking functions call this before sending events.
 */
export const ensureGA = (): boolean => {
  if (typeof window === 'undefined') return false
  if (!hasAnalyticsConsent()) return false
  return !!window.gtag
}

/**
 * Initialize GA with user properties
 * Should be called after user login/when user context is available.
 * User IDs are hashed for GDPR/CCPA compliance.
 */
export const initAnalytics = async (user: UserProperties) => {
  if (!ensureGA()) {
    return
  }

  // Hash user ID and org ID for privacy (GDPR/CCPA compliance)
  const hashedUserId = user.user_id
    ? await hashUserId(user.user_id)
    : 'anonymous'
  const hashedOrgId = user.org_id ? await hashUserId(user.org_id) : 'unknown'

  // Set user properties
  window.gtag('set', 'user_properties', {
    user_id: hashedUserId,
    user_role: user.user_role || 'unknown',
    org_id: hashedOrgId,
    subscription_type: user.subscription_type || 'none',
    plan_name: user.plan_name || 'none',
  })
}

/**
 * Track page views
 */
export const trackPageView = (url: string, title: string) => {
  if (!ensureGA()) return

  window.gtag('event', 'page_view', {
    page_title: title,
    page_location: url,
    page_path: window.location.pathname,
  })
}

/**
 * Track custom events with category and label
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  params?: EventParams
) => {
  if (!ensureGA()) return

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...params,
  })
}

/**
 * Track billing-specific events
 */
export const trackBillingEvent = (
  event:
    | 'view_plans'
    | 'start_checkout'
    | 'complete_checkout'
    | 'cancel_subscription'
    | 'update_payment_method'
    | 'view_stripe_portal',
  params?: EventParams
) => {
  if (isCompOrg()) return
  trackEvent(event, 'billing', undefined, undefined, params)
}

/**
 * Track user interaction events (clicks, form submissions, etc.)
 */
export const trackUserInteraction = (
  element: string,
  action: 'click' | 'submit' | 'change',
  label?: string
) => {
  trackEvent(
    action,
    'user_interaction',
    `${element}${label ? ` - ${label}` : ''}`
  )
}

/**
 * Track conversion events with revenue
 */
export const trackConversion = (
  conversion:
    | 'trial_signup'
    | 'paid_subscription'
    | 'plan_upgrade'
    | 'plan_downgrade',
  value?: number,
  params?: EventParams
) => {
  if (isCompOrg()) return
  trackEvent(conversion, 'conversion', undefined, value, params)
}

/**
 * Track errors
 */
export const trackError = (error: string, fatal: boolean = false) => {
  if (!ensureGA()) return

  window.gtag('event', 'exception', {
    description: error,
    fatal: fatal,
  })

  if (import.meta.env.DEV) {
    console.error('[GA] Error:', error, fatal)
  }
}

/**
 * Track plan view events on pricing page
 */
export const trackViewPlan = (planName: string, billingCycle: string) => {
  trackBillingEvent('view_plans', {
    plan_name: planName,
    billing_cycle: billingCycle,
  })
}

/**
 * Track checkout start
 */
export const trackCheckoutStart = (planName: string, billingCycle: string) => {
  trackBillingEvent('start_checkout', {
    plan_name: planName,
    billing_cycle: billingCycle,
  })
}

/**
 * Track successful checkout/subscription creation
 */
export const trackCheckoutSuccess = (
  planName: string,
  amount: number,
  billingCycle: string,
  isUpgrade: boolean = false
) => {
  const conversionType = isUpgrade ? 'plan_upgrade' : 'paid_subscription'
  trackConversion(conversionType, amount, {
    plan_name: planName,
    billing_cycle: billingCycle,
  })

  trackBillingEvent('complete_checkout', {
    plan_name: planName,
    amount: amount,
    billing_cycle: billingCycle,
  })
}

/**
 * Track subscription cancellation
 */
export const trackCancelSubscription = (
  planName: string,
  billingCycle: string
) => {
  trackBillingEvent('cancel_subscription', {
    plan_name: planName,
    billing_cycle: billingCycle,
  })
}

/**
 * Track payment method update
 */
export const trackUpdatePaymentMethod = () => {
  trackBillingEvent('update_payment_method')
}

/**
 * Track Stripe portal opening
 */
export const trackStripePortalOpen = () => {
  trackBillingEvent('view_stripe_portal')
}

/**
 * Track navigation clicks
 */
export const trackNavigation = (label: string) => {
  trackUserInteraction('navigation', 'click', label)
}

/**
 * Track button clicks
 */
export const trackButtonClick = (label: string) => {
  trackUserInteraction('button', 'click', label)
}

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName: string) => {
  trackUserInteraction(formName, 'submit')
}

/**
 * Track domain addition
 */
export const trackDomainAdded = () => {
  trackEvent('domain_added', 'domain_management')
}

/**
 * Track domain deletion
 */
export const trackDomainDeleted = () => {
  trackEvent('domain_deleted', 'domain_management')
}

/**
 * Track analytics page view
 */
export const trackAnalyticsPageView = () => {
  trackEvent('view_analytics', 'analytics')
}

/**
 * Track SPF validator usage
 */
export const trackSPFValidatorUsage = (domainCount: number) => {
  trackEvent('spf_validator_used', 'tools', undefined, domainCount)
}

/**
 * Track webhook configuration
 */
export const trackWebhookConfigured = (webhookType: string) => {
  trackEvent('webhook_configured', 'webhooks', webhookType)
}
